import { PROTOCOL, type Card, type RoomView } from '@uno/shared';
import { useEffect, useRef, useState } from 'react';
import { useT } from '../hooks';
import { fmt, type Translation } from '../i18n';
import { net, type FeedEvent } from '../net';

interface Props {
  room: RoomView;
  feed: FeedEvent[];
  onClose: () => void;
}

/** Localised card name, e.g. "red 7", "blue Skip", "+4". */
export function cardName(card: Card, t: Translation): string {
  const colour =
    card.color === 'wild' ? '' : `${t.cards[card.color as 'red' | 'yellow' | 'green' | 'blue']} `;
  switch (card.kind) {
    case 'number':
      return `${colour}${card.value}`;
    case 'skip':
      return `${colour}${t.cards.skip}`;
    case 'reverse':
      return `${colour}${t.cards.reverse}`;
    case 'draw2':
      return `${colour}${t.cards.draw2}`;
    case 'wild':
      return t.cards.wildCard;
    case 'wild4':
      return t.cards.wild4;
  }
}

/** Fixed 24-hour clock — the log is scanned, not read as prose. */
function clock(at: number): string {
  const d = new Date(at);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const LS_TIMESTAMPS = 'uno:log:ts';

interface Line {
  id: number;
  at: number;
  text: string;
  card?: Card;
  /** Table talk renders differently from narration. */
  speaker?: string;
  mine: boolean;
}

/**
 * Renders the event feed as readable history.
 *
 * Only events that describe a *move* are shown — turn changes and internal
 * transitions would drown the interesting lines in noise.
 */
function toLines(room: RoomView, feed: FeedEvent[], t: Translation): Line[] {
  const nameOf = (id: string) => room.players.find((p) => p.id === id)?.nickname ?? t.events.someone;
  const me = room.self?.id;
  const lines: Line[] = [];

  for (const { id, at, event: e } of feed) {
    const push = (text: string, who?: string, extra?: { card?: Card; speaker?: string }) =>
      lines.push({ id, at, text, mine: who === me, ...extra });

    switch (e.type) {
      case 'chat':
        push(e.text, e.playerId, { speaker: nameOf(e.playerId) });
        break;
      case 'game_started':
        push(t.log.started);
        break;
      case 'played':
        push(fmt(t.log.played, { name: nameOf(e.playerId), card: '' }).trim(), e.playerId, { card: e.card });
        break;
      case 'drew':
        push(fmt(t.log.drew, { name: nameOf(e.playerId), count: e.count }), e.playerId);
        break;
      case 'skipped':
        push(fmt(t.log.skipped, { name: nameOf(e.playerId) }), e.playerId);
        break;
      case 'reversed':
        push(t.log.reversed);
        break;
      case 'uno_called':
        push(
          e.playerId === me ? t.events.unoCalledYou : fmt(t.events.unoCalledOther, { name: nameOf(e.playerId) }),
          e.playerId,
        );
        break;
      case 'judged':
        push(
          fmt(t.events.judged, { accuser: nameOf(e.accuserId), target: nameOf(e.targetId), count: e.penalty }),
          e.targetId,
        );
        break;
      case 'challenge_resolved':
        push(
          e.bluffed
            ? fmt(t.events.challengeBluff, { name: nameOf(e.targetId), count: e.penalty })
            : fmt(t.events.challengeFailed, { name: nameOf(e.accuserId), count: e.penalty }),
        );
        break;
      case 'afk_autoplay':
        push(e.playerId === me ? t.events.afkYou : fmt(t.events.afkOther, { name: nameOf(e.playerId) }), e.playerId);
        break;
      case 'player_forfeited':
        push(fmt(t.events.forfeited, { name: nameOf(e.playerId) }), e.playerId);
        break;
      case 'player_joined':
        push(fmt(t.log.joined, { name: e.nickname }), e.playerId);
        break;
      case 'player_left':
        push(fmt(t.log.left, { name: e.nickname }), e.playerId);
        break;
      case 'host_changed':
        push(fmt(t.events.hostChanged, { name: nameOf(e.playerId) }), e.playerId);
        break;
      case 'deck_reshuffled':
        push(t.events.reshuffled);
        break;
      case 'game_over':
        push(e.winnerId ? fmt(t.events.winner, { name: nameOf(e.winnerId) }) : t.events.gameOver, e.winnerId ?? undefined);
        break;
      default:
        break; // turn changes, colour picks, window bookkeeping — too noisy
    }
  }
  return lines;
}

export function PlayLog({ room, feed, onClose }: Props) {
  const t = useT();
  const lines = toLines(room, feed, t);
  const listRef = useRef<HTMLOListElement>(null);
  const [draft, setDraft] = useState('');
  const [showTimes, setShowTimes] = useState(() => {
    try {
      return localStorage.getItem(LS_TIMESTAMPS) === '1';
    } catch {
      return false;
    }
  });

  const toggleTimes = () => {
    setShowTimes((on) => {
      try {
        localStorage.setItem(LS_TIMESTAMPS, on ? '0' : '1');
      } catch {
        /* private mode — the preference just won't persist */
      }
      return !on;
    });
  };

  // Newest entries are at the bottom; follow them.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    net.sendChat(text);
    setDraft('');
  };

  return (
    <aside className="playlog" role="log" aria-live="polite">
      <div className="playlog__head">
        <span>{t.log.title}</span>
        <span className="playlog__tools">
          <button
            type="button"
            className={`playlog__tool ${showTimes ? 'is-on' : ''}`}
            onClick={toggleTimes}
            aria-pressed={showTimes}
            title={t.log.timestamps}
          >
            🕑
          </button>
          <button type="button" className="playlog__tool" onClick={onClose} aria-label={t.log.close}>
            ✕
          </button>
        </span>
      </div>

      {lines.length === 0 ? (
        <p className="playlog__empty">{t.log.empty}</p>
      ) : (
        <ol className="playlog__list" ref={listRef}>
          {lines.map((line, i) => (
            <li key={`${line.id}-${i}`} className={`${line.mine ? 'is-me' : ''} ${line.speaker ? 'is-chat' : ''}`}>
              {showTimes && <span className="playlog__time">[{clock(line.at)}]</span>}
              {line.speaker && <b className="playlog__speaker">{line.speaker}:</b>}{' '}
              {line.text}
              {line.card && (
                <>
                  {' '}
                  <span className={`logcard logcard--${line.card.color}`}>{cardName(line.card, t)}</span>
                </>
              )}
            </li>
          ))}
        </ol>
      )}

      <form
        className="playlog__chat"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          className="input"
          name="chat"
          value={draft}
          maxLength={PROTOCOL.CHAT_MAX}
          placeholder={t.log.chatPlaceholder}
          aria-label={t.log.chatPlaceholder}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="btn btn--sm" disabled={!draft.trim()}>
          {t.log.send}
        </button>
      </form>
    </aside>
  );
}
